import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import { Button, Col, Input, Row, Typography } from "antd";
import {
  CloseOutlined,
  PlusOutlined,
  MinusOutlined,
  AimOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import { GOOGLE_MAPS_CONFIG } from "../../../commonUtils/commonUtils";
import "./GoogleMapsModal.css";

const { Text } = Typography;

const GoogleMapsModal = ({
  visible,
  initialLocation,
  onClose,
  onConfirmAndProceed,
  address,
  setAddress = () => {},
}) => {
  const { isLoaded, loadError } = useJsApiLoader(GOOGLE_MAPS_CONFIG);

  const defaultLocation = useMemo(() => ({ lat: 20.5937, lng: 78.9629 }), []);
  const [systemLocation, setSystemLocation] = useState(null);
  const [mapCenter, setMapCenter] = useState(defaultLocation);
  const [zoom, setZoom] = useState(15);
  const [selectedLocation, setSelectedLocation] = useState(defaultLocation);
  const [localAddress, setLocalAddress] = useState(null);
  const [map, setMap] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [searchCache] = useState(new Map());
  const searchTimeoutRef = useRef(null);

  // Geocoding cache and loading state
  const [geocodingCache] = useState(new Map());
  const [isGeocodingLoading, setIsGeocodingLoading] = useState(false);
  const geocodingTimeoutRef = useRef(null);
  const [placeDetailsCache] = useState(new Map());
  const hasInitialized = useRef(false);

  // Debounced search function
  const handleSearchChange = async (e) => {
    const value = e.target.value;
    setSearchValue(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!value.trim()) {
      setSuggestions([]);
      return;
    }

    if (searchCache.has(value)) {
      setSuggestions(searchCache.get(value));
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      if (isLoaded && window.google && value) {
        const service = new window.google.maps.places.AutocompleteService();
        service.getPlacePredictions(
          {
            input: value,
            componentRestrictions: { country: "IN" },
            types: ["geocode"],
          },
          (predictions, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
              searchCache.set(value, predictions);
              setSuggestions(predictions);
            } else {
              setSuggestions([]);
            }
          }
        );
      } else {
        setSuggestions([]);
      }
    }, 300);
  };

  // Optimized address lookup with caching and debouncing
  const handleAddressLookup = useCallback(
    async (location) => {
      if (!isLoaded || !window.google || isGeocodingLoading) {
        return;
      }

      const cacheKey = `${location.lat.toFixed(6)},${location.lng.toFixed(6)}`;

      if (geocodingCache.has(cacheKey)) {
        const cachedAddress = geocodingCache.get(cacheKey);
        setAddress(cachedAddress);
        setLocalAddress(cachedAddress);
        setSearchValue(cachedAddress.formatted_address);
        return;
      }

      if (geocodingTimeoutRef.current) {
        clearTimeout(geocodingTimeoutRef.current);
      }

      geocodingTimeoutRef.current = setTimeout(async () => {
        setIsGeocodingLoading(true);
        try {
          const geocoder = new window.google.maps.Geocoder();
          const result = await geocoder.geocode({ location });

          if (result.results[0]) {
            const addressData = {
              formatted_address: result.results[0].formatted_address,
              lat: location.lat,
              lng: location.lng,
            };

            geocodingCache.set(cacheKey, addressData);
            setAddress(addressData);
            setLocalAddress(addressData);
            setSearchValue(result.results[0].formatted_address);
          }
        } catch (error) {
          console.error("Error getting address:", error);
        } finally {
          setIsGeocodingLoading(false);
        }
      }, 500);
    },
    [setAddress, isLoaded, isGeocodingLoading, geocodingCache]
  );

  // Combined function to set location and get address
  const setLocationAndGetAddress = useCallback(
    (location) => {
      setSelectedLocation(location);
      setMapCenter(location);
      handleAddressLookup(location);
    },
    [handleAddressLookup]
  );

  const handleSuggestionSelect = useCallback(
    (suggestion) => {
      if (!isLoaded || !window.google) {
        return;
      }

      if (placeDetailsCache.has(suggestion.place_id)) {
        const cachedPlace = placeDetailsCache.get(suggestion.place_id);
        setSelectedLocation(cachedPlace.location);
        setMapCenter(cachedPlace.location);
        setSearchValue(cachedPlace.addressData.formatted_address);
        setSuggestions([]);
        setAddress(cachedPlace.addressData);
        setLocalAddress(cachedPlace.addressData);
        return;
      }

      const service = new window.google.maps.places.PlacesService(
        document.createElement("div")
      );
      service.getDetails(
        {
          placeId: suggestion.place_id,
          fields: ["geometry", "formatted_address"],
        },
        (place, status) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            place.geometry
          ) {
            const location = {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
            };
            const addressData = {
              formatted_address: place.formatted_address || suggestion.description,
              lat: location.lat,
              lng: location.lng,
            };

            placeDetailsCache.set(suggestion.place_id, {
              location,
              addressData,
            });

            setSelectedLocation(location);
            setMapCenter(location);
            setSearchValue(suggestion.description);
            setSuggestions([]);
            setAddress(addressData);
            setLocalAddress(addressData);
          }
        }
      );
    },
    [isLoaded, setAddress, placeDetailsCache]
  );

  // Handle initial location setup when modal opens - FIXED to prevent infinite loop
  useEffect(() => {
    if (visible && isLoaded && !hasInitialized.current) {
      hasInitialized.current = true;
      
      // Priority 1: Use provided address if available
      if (address && address.lat && address.lng) {
        const location = {
          lat: Number(address.lat),
          lng: Number(address.lng),
        };
        setSelectedLocation(location);
        setMapCenter(location);
        setLocalAddress(address);
        setSystemLocation(location);

        if (address.formatted_address) {
          setSearchValue(address.formatted_address);
        }
        return;
      }

      // Priority 2: Get current location from browser
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const location = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            setSystemLocation(location);
            setLocationAndGetAddress(location);
          },
          (error) => {
            console.log("Geolocation error:", error.message);
            setSystemLocation(defaultLocation);
            setSelectedLocation(defaultLocation);
            setMapCenter(defaultLocation);
            handleAddressLookup(defaultLocation);
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 300000,
          }
        );
      } else {
        setSystemLocation(defaultLocation);
        setSelectedLocation(defaultLocation);
        setMapCenter(defaultLocation);
        handleAddressLookup(defaultLocation);
      }
    }
    
    // Reset when modal closes
    if (!visible) {
      hasInitialized.current = false;
      setSystemLocation(null);
      setSelectedLocation(defaultLocation);
      setMapCenter(defaultLocation);
      setLocalAddress(null);
      setSearchValue("");
      setSuggestions([]);
      setIsGeocodingLoading(false);

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (geocodingTimeoutRef.current) {
        clearTimeout(geocodingTimeoutRef.current);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, isLoaded]);

  // Cleanup timeouts on component unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (geocodingTimeoutRef.current) {
        clearTimeout(geocodingTimeoutRef.current);
      }
    };
  }, []);

  // Ensure marker position updates when selectedLocation changes
  useEffect(() => {
    if (map && map.customMarker && selectedLocation) {
      map.customMarker.setPosition(selectedLocation);
    }
  }, [map, selectedLocation]);

  // Function to extract city, state, and pincode from formatted address
  const parseAddressComponents = (formattedAddress) => {
    if (!formattedAddress) {
      return { city: "", state: "", pincode: "" };
    }

    const parts = formattedAddress.split(",").map((part) => part.trim());
    let city = "";
    let state = "";
    let pincode = "";

    const pincodeRegex = /\b\d{6}\b/;
    const stateAndPincodeIndex = parts.findIndex((part) => pincodeRegex.test(part));

    if (stateAndPincodeIndex !== -1) {
      const stateAndPincodePart = parts[stateAndPincodeIndex];
      const pincodeMatch = stateAndPincodePart.match(pincodeRegex);
      if (pincodeMatch) {
        pincode = pincodeMatch[0];
        state = stateAndPincodePart.replace(pincodeRegex, "").trim();
      }

      if (stateAndPincodeIndex > 0) {
        city = parts[stateAndPincodeIndex - 1];
      }
    }

    return { city, state, pincode };
  };

  // Function to get current location
  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };

          setSelectedLocation(location);
          setMapCenter(location);
          setSystemLocation(location);
          setSearchValue("Getting current location...");
          handleAddressLookup(location);
        },
        (error) => {
          console.error("Error getting current location:", error);
          switch (error.code) {
            case error.PERMISSION_DENIED:
              alert("Location access denied by user. Please enable location permission in your browser.");
              break;
            case error.POSITION_UNAVAILABLE:
              alert("Location information is unavailable.");
              break;
            case error.TIMEOUT:
              alert("Location request timed out. Please try again.");
              break;
            default:
              alert("An unknown error occurred while retrieving location.");
              break;
          }
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 60000,
        }
      );
    } else {
      alert("Geolocation is not supported by this browser.");
    }
  };

  if (!visible) return null;

  if (loadError) {
    return <div>Error loading Google Maps</div>;
  }

  if (!isLoaded) {
    return <div>Loading Google Maps...</div>;
  }

  return (
    <div className="google-maps-modal-overlay">
      <div className="google-maps-modal-container">
        <div className="google-maps-modal-header">
          <Row className="modal-title-parent">
            <Text className="modal-title-content">Confirm map pin location</Text>
          </Row>
          <Button type="text" onClick={onClose} icon={<CloseOutlined />} />
        </div>
        
        <div className="google-maps-container">
          {/* Search box overlay */}
          <div className="search-overlay">
            <Input
              type="text"
              className="search-input"
              placeholder="Search for area, street name..."
              prefix={<EnvironmentOutlined style={{ color: "#666" }} />}
              value={searchValue}
              onChange={handleSearchChange}
            />
            {suggestions.length > 0 && (
              <div className="suggestions-dropdown">
                {suggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    className="suggestion-item"
                    onClick={() => handleSuggestionSelect(suggestion)}
                  >
                    {suggestion.description}
                  </div>
                ))}
              </div>
            )}
          </div>

          <GoogleMap
            center={mapCenter}
            zoom={zoom}
            onLoad={(mapInstance) => {
              setMap(mapInstance);

              if (mapInstance.customMarker) {
                mapInstance.customMarker.setMap(null);
              }

              if (window.google && window.google.maps) {
                const customMarkerSVG = `<svg xmlns="http://www.w3.org/2000/svg" width="38" height="56" viewBox="0 0 38 56" fill="none">
                  <path opacity="0.8" d="M18.5512 56.0038C24.5311 56.0038 29.3788 54.6143 29.3788 52.9003C29.3788 51.1863 24.5311 49.7969 18.5512 49.7969C12.5713 49.7969 7.72363 51.1863 7.72363 52.9003C7.72363 54.6143 12.5713 56.0038 18.5512 56.0038Z" fill="#00DE9D"/>
                  <path d="M18.5944 0C8.34168 0 0 8.34113 0 18.5932C0 26.9743 5.55225 34.2588 13.5705 36.4985L17.6935 52.2679C17.7999 52.6777 18.1713 52.9638 18.5944 52.9638C19.0176 52.9638 19.389 52.6777 19.4954 52.2679L23.6184 36.4985C31.6367 34.2588 37.1889 26.9743 37.1889 18.5932C37.1889 8.34113 28.8472 0 18.5944 0Z" fill="#1C1C1C"/>
                  <ellipse cx="18.6207" cy="18.483" rx="6.06897" ry="6.06898" fill="white"/>
                </svg>`;

                const marker = new window.google.maps.Marker({
                  position: selectedLocation,
                  map: mapInstance,
                  title: "Selected Location",
                  icon: {
                    url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(customMarkerSVG),
                    scaledSize: new window.google.maps.Size(24, 35),
                    anchor: new window.google.maps.Point(12, 35),
                    origin: new window.google.maps.Point(0, 0),
                  },
                  optimized: false,
                });

                mapInstance.customMarker = marker;
              }
            }}
            onUnmount={() => {
              if (map && map.customMarker) {
                map.customMarker.setMap(null);
                map.customMarker = null;
              }
              setMap(null);
            }}
            onDrag={() => {
              if (map) {
                const center = map.getCenter();
                const location = {
                  lat: center.lat(),
                  lng: center.lng(),
                };
                setSelectedLocation(location);

                // Update marker position directly - no need for separate useEffect
                if (map.customMarker) {
                  map.customMarker.setPosition(location);
                }
              }
            }}
            onDragEnd={() => {
              if (map && !isGeocodingLoading) {
                const center = map.getCenter();
                const location = {
                  lat: center.lat(),
                  lng: center.lng(),
                };
                setLocationAndGetAddress(location);
              }
            }}
            options={{
              zoomControl: false,
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: false,
              rotateControl: false,
              scaleControl: false,
              panControl: false,
              gestureHandling: "greedy",
              scrollwheel: true,
              disableDefaultUI: true,
            }}
            mapContainerStyle={{ height: "100%", width: "100%" }}
          />

          {/* Map controls */}
          <div className="map-controls">
            <Button
              icon={<PlusOutlined />}
              onClick={() => setZoom(Math.min(zoom + 1, 20))}
              className="map-control-btn"
            />
            <Button
              icon={<MinusOutlined />}
              onClick={() => setZoom(Math.max(zoom - 1, 1))}
              className="map-control-btn"
            />
            <Button
              icon={<AimOutlined />}
              onClick={() => selectedLocation && setMapCenter(selectedLocation)}
              className="map-control-btn"
            />
            <Button
              icon={<EnvironmentOutlined />}
              onClick={getCurrentLocation}
              title="Current Location"
              className="map-control-btn current-location-btn"
            />
          </div>
        </div>

        <div className="google-maps-modal-footer">
          <div className="address-section">
            <Text className="address-label">Order will be delivered here</Text>
            <div className="address-display">
              <EnvironmentOutlined className="location-icon" />
              <div className="address-text">
                {address?.formatted_address || localAddress?.formatted_address
                  ? address?.formatted_address || localAddress?.formatted_address
                  : isGeocodingLoading
                    ? "Detecting address..."
                    : systemLocation && systemLocation.lat && systemLocation.lng
                      ? "Detecting address..."
                      : "Move the map or pin to select your location"}
              </div>
            </div>
            
            <Col className="confirm-button-container">
              <Button
                className="confirm-button"
                onClick={() => {
                  const baseAddress = address || localAddress || {
                    formatted_address: "Current location",
                    lat: selectedLocation.lat,
                    lng: selectedLocation.lng,
                  };

                  const addressComponents = parseAddressComponents(baseAddress.formatted_address);
                  const currentAddress = {
                    ...baseAddress,
                    city: addressComponents.city,
                    state: addressComponents.state,
                    pincode: addressComponents.pincode,
                  };

                  onConfirmAndProceed(currentAddress);
                }}
              >
                CONFIRM AND PROCEED
              </Button>
            </Col>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoogleMapsModal;